import { dungApiThat, goiApi } from '../api/http';
import { mayChuLoaiChungTu } from '../../mock/ocr';
import type {
  DocumentType,
  OcrColumn,
  OcrColumnInput,
  OcrColumnPatch,
  OcrField,
  OcrFieldInput,
  OcrFieldPatch,
  OcrSchemaCreate,
  OcrSchemaDetail,
  OcrSchemaPage,
  OcrSchemaPatch,
  OcrSchemaStats,
  OcrTable,
  OcrTableInput,
  OcrTablePatch,
} from '../../modules/cau-hinh-ocr/types';
import { GOC_OCR, giaLap, giaLapDanhSach, kiemTrang, maHoa } from './ocr-chung';

const GOC = `${GOC_OCR}/schemas`;
const TRANG_RONG: OcrSchemaPage = { items: [], next_cursor: null };
const THONG_KE_RONG: OcrSchemaStats = { total_schemas: 0, active_schemas: 0, total_fields: 0, total_tables: 0 };

export interface LocLoaiChungTu {
  search?: string;
  type?: DocumentType;
  is_active?: boolean;
  limit: number;
  cursor?: string;
}

const goc = (id: string) => `${GOC}/${maHoa(id)}`;

/**
 * Cấu hình loại chứng từ (module `ocr`, đợt 1–2): loại chứng từ, trường, bảng, cột. Mỗi hàm chọn
 * **lúc gọi** giữa API thật và máy chủ giả (`mock/ocr.ts`). Lỗi ràng buộc `400 INVALID_ARGUMENT`
 * mang `errors[].pointer` (`#/code`, `#/field_key`…) để form hiện dưới đúng ô.
 */
export const loaiChungTuRepository = {
  /** `GET /schemas` — mới tạo trước, phân trang con trỏ. */
  danhSach: (loc: LocLoaiChungTu): Promise<OcrSchemaPage> =>
    dungApiThat()
      ? goiApi<OcrSchemaPage>(GOC, {
          query: { ...loc, is_active: loc.is_active === undefined ? undefined : String(loc.is_active) },
        }).then(kiemTrang)
      : giaLapDanhSach(() => mayChuLoaiChungTu.danhSach(loc), TRANG_RONG),

  /** `GET /schemas/stats`. */
  thongKe: (): Promise<OcrSchemaStats> =>
    dungApiThat()
      ? goiApi<OcrSchemaStats>(`${GOC}/stats`)
      : giaLapDanhSach(() => mayChuLoaiChungTu.thongKe(), THONG_KE_RONG),

  /** `GET /schemas/{id}` — kèm trường, bảng, cột. */
  chiTiet: (id: string): Promise<OcrSchemaDetail> =>
    dungApiThat() ? goiApi<OcrSchemaDetail>(goc(id)) : giaLap(() => mayChuLoaiChungTu.chiTiet(id)),

  /** `GET /schemas/code/{code}` — màn nhận dạng mở theo mã trên URL. */
  theoMa: (code: string): Promise<OcrSchemaDetail> =>
    dungApiThat()
      ? goiApi<OcrSchemaDetail>(`${GOC}/code/${maHoa(code)}`)
      : giaLap(() => mayChuLoaiChungTu.theoMa(code)),

  /** `POST /schemas` → `201`. Trùng mã `409 OCR_SCHEMA_CODE_EXISTS` kèm `#/code`. */
  tao: (du: OcrSchemaCreate): Promise<OcrSchemaDetail> =>
    dungApiThat()
      ? goiApi<OcrSchemaDetail>(GOC, { method: 'POST', body: du })
      : giaLap(() => mayChuLoaiChungTu.tao(du)),

  /** `PATCH /schemas/{id}` — chỉ các cột cấp loại chứng từ. */
  sua: (id: string, du: OcrSchemaPatch): Promise<OcrSchemaDetail> =>
    dungApiThat()
      ? goiApi<OcrSchemaDetail>(goc(id), { method: 'PATCH', body: du })
      : giaLap(() => mayChuLoaiChungTu.sua(id, du)),

  /** `DELETE /schemas/{id}` → `204`. Đang có chứng từ: `409 OCR_SCHEMA_IN_USE`. */
  xoa: (id: string): Promise<void> =>
    dungApiThat() ? goiApi<void>(goc(id), { method: 'DELETE' }) : giaLap(() => mayChuLoaiChungTu.xoa(id)),

  themTruong: (id: string, du: OcrFieldInput): Promise<OcrField> =>
    dungApiThat()
      ? goiApi<OcrField>(`${goc(id)}/fields`, { method: 'POST', body: du })
      : giaLap(() => mayChuLoaiChungTu.themTruong(id, du)),

  suaTruong: (id: string, fieldId: string, du: OcrFieldPatch): Promise<OcrField> =>
    dungApiThat()
      ? goiApi<OcrField>(`${goc(id)}/fields/${maHoa(fieldId)}`, { method: 'PATCH', body: du })
      : giaLap(() => mayChuLoaiChungTu.suaTruong(id, fieldId, du)),

  /** Trường đã có giá trị ở chứng từ: `409 OCR_SCHEMA_IN_USE`. */
  xoaTruong: (id: string, fieldId: string): Promise<void> =>
    dungApiThat()
      ? goiApi<void>(`${goc(id)}/fields/${maHoa(fieldId)}`, { method: 'DELETE' })
      : giaLap(() => mayChuLoaiChungTu.xoaTruong(id, fieldId)),

  themBang: (id: string, du: OcrTableInput): Promise<OcrTable> =>
    dungApiThat()
      ? goiApi<OcrTable>(`${goc(id)}/tables`, { method: 'POST', body: du })
      : giaLap(() => mayChuLoaiChungTu.themBang(id, du)),

  /** Đổi `table_key` khi bảng đã có dòng: `409`. */
  suaBang: (id: string, tableId: string, du: OcrTablePatch): Promise<OcrTable> =>
    dungApiThat()
      ? goiApi<OcrTable>(`${goc(id)}/tables/${maHoa(tableId)}`, { method: 'PATCH', body: du })
      : giaLap(() => mayChuLoaiChungTu.suaBang(id, tableId, du)),

  xoaBang: (id: string, tableId: string): Promise<void> =>
    dungApiThat()
      ? goiApi<void>(`${goc(id)}/tables/${maHoa(tableId)}`, { method: 'DELETE' })
      : giaLap(() => mayChuLoaiChungTu.xoaBang(id, tableId)),

  themCot: (id: string, tableId: string, du: OcrColumnInput): Promise<OcrColumn> =>
    dungApiThat()
      ? goiApi<OcrColumn>(`${goc(id)}/tables/${maHoa(tableId)}/columns`, { method: 'POST', body: du })
      : giaLap(() => mayChuLoaiChungTu.themCot(id, tableId, du)),

  suaCot: (id: string, tableId: string, columnId: string, du: OcrColumnPatch): Promise<OcrColumn> =>
    dungApiThat()
      ? goiApi<OcrColumn>(`${goc(id)}/tables/${maHoa(tableId)}/columns/${maHoa(columnId)}`, { method: 'PATCH', body: du })
      : giaLap(() => mayChuLoaiChungTu.suaCot(id, tableId, columnId, du)),

  xoaCot: (id: string, tableId: string, columnId: string): Promise<void> =>
    dungApiThat()
      ? goiApi<void>(`${goc(id)}/tables/${maHoa(tableId)}/columns/${maHoa(columnId)}`, { method: 'DELETE' })
      : giaLap(() => mayChuLoaiChungTu.xoaCot(id, tableId, columnId)),
};
